import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "JavaScript can fetch() now and it's not THAT great",
  "description": "",
  "published": "2016-08-12T00:00:00.000Z",
  "redirect_from": ["/blog/javascript-can-fetch-now-and-its-not-that-great/swizec/6923"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`As I was researching `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/i-broke-ajax-in-chrome-52-%F0%9F%98%B1/swizec/6914"
      }}>{`that Chrome 52 bug`}</a>{` yesterday, my friend `}<a parentName="p" {...{
        "href": "https://twitter.com/Smotko"
      }}>{`Smotko`}</a>{` showed me something cool: `}<inlineCode parentName="p">{`fetch()`}</inlineCode>{`.`}</p>
    <p>{`It’s a native JavaScript function for AJAX GET requests. I don’t know what stage it is, but Chrome, Edge, Firefox, and Opera all support it. Safari doesn’t. Nor do any default mobile browsers, only Android Chrome.`}</p>
    <p>{`You can use this `}<a parentName="p" {...{
        "href": "https://gist.github.com/Couto/b29676dd1ab8714a818f"
      }}>{`Babel fetch polyfill`}</a>{` to bridge the gap. I wonder if that’s good enough to convince our CTO to let me use this. ?`}</p>
    <p>{`Then again, why? Oh yeah, that strange Chrome 52 bug doesn’t happen and all I’d have to do was change that one API wrapper function that we use for everything … and thus change our whole codebase … and wreak havoc. ?`}</p>
    <p>{`But The Bug doesn’t happen! Let me show you.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/fetch.gif",
        "alt": "fetch()"
      }}></img></p>
    <p>{`Ugh, promises don’t work in the console. That’s a bummer … never did like promises. It’s really just weird syntax sugar for callbacks.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`fetch("bla.json").then((response) => {
  console.log(response.ok); // prints true
  console.log(response.body); // ReadableStream O.o
});
`}</code></pre>
    <p><inlineCode parentName="p">{`fetch()`}</inlineCode>{` issues an AJAX request to the server. When it’s done, it triggers the `}<inlineCode parentName="p">{`then`}</inlineCode>{` part of its promise and gives it a `}<inlineCode parentName="p">{`response`}</inlineCode>{` object. This object can do many useful things, one of which is to tell you if it was successful. Another is to give you a ReadableStream.`}</p>
    <p>{`You probably don’t care about readable streams, so there are functions to parse JSON or get raw text. We’re in the brave new world of promises, so those are promises too.`}</p>
    <p>{`They work like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`fetch("bla.json").then((response) => {
  response.json().then((json) => console.log(json)); // Object {id: 1, hai: "hello"}

  response.text().then((text) => console.log(text)); // nothing
});
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`json().then()`}</inlineCode>{` combination prints a JavaScript object parsed from our data. Which feels roundabout, but the internet tells me that promises are better than The Callback Hell.`}</p>
    <p>{`This:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`fetch(url).then((data) => data.body.json().then((real_data) => doStuff));
`}</code></pre>
    <p>{`Is supposed to be better than:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`request.get("bla.json").end((err, res) => console.log(res.body));
`}</code></pre>
    <p>{`¯`}{`_`}{`(ツ)`}{`_`}{`/¯`}</p>
    <p>{`But lets get back to the story: You can’t read the fetch response body twice. See that `}<inlineCode parentName="p">{`.text()`}</inlineCode>{` call? That throws an error saying that oh hey, the body has already been read, so you can’t do it again.`}</p>
    <p>{`Maybe there’s no good reason you’d want to do that, but what if I `}<em parentName="p">{`do`}</em>{` want to?`}</p>
    <p>{`There’s an even worse caveat, though. Let me show you what happens when your `}<inlineCode parentName="p">{`fetch()`}</inlineCode>{` call returns a 404.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`fetch("nope.json")
  .catch(() => console.log("something went wrong")) // no error
  .then((res) => console.log(res.ok)); // but not okay either
`}</code></pre>
    <p>{`With promises, `}<inlineCode parentName="p">{`catch()`}</inlineCode>{` should give us a chance to catch errors. This keeps our code cleaner because `}<inlineCode parentName="p">{`then()`}</inlineCode>{` can focus on the happy path.`}</p>
    <p>{`Somebody decided a 404 error is not a good enough reason to reject the promise. You still have to put a lot of error handling into your happy path.`}</p>
    <p>{`?`}</p>
    <p>{`But `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/i-broke-ajax-in-chrome-52-%F0%9F%98%B1/swizec/6914"
      }}>{`The Bug`}</a>{` doesn’t happen! I tried. You can repeat the same request in the `}<inlineCode parentName="p">{`then()`}</inlineCode>{` callback and its `}<inlineCode parentName="p">{`then()`}</inlineCode>{` callback works just fine.`}</p>
    <p>{`There is an official explanation for how the bug happens in the `}<a parentName="p" {...{
        "href": "https://bugs.chromium.org/p/chromium/issues/detail?id=633696"
      }}>{`bug report`}</a>{` from 8 days ago. A `}<a parentName="p" {...{
        "href": "https://codereview.chromium.org/2210473002/"
      }}>{`fix also exists`}</a>{`, but it’s not merged yet.`}</p>
    <p>{`This debacle makes me wish I was braver when first seeing this behavior 3 months ago. Way back when it was Chrome Canary and I thought surely someone would fix it.`}</p>
    <p>{`What a strange ride it’s been.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      